import { useEffect, useMemo, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react'
import { Calendar, Download, FileText, ImagePlus, MessageSquare, Paperclip, Pencil, Plus, Star, Trash2, UserRound, X } from 'lucide-react'
import { pb } from '../../lib/pocketbase'
import { resolveFileUrl } from '../../lib/files'
import { useAuth } from '../../state/AuthContext'

type TaskStatus = 'backlog' | 'todo' | 'doing' | 'done' | 'blocked'

interface KanbanTask {
  id: string
  title: string
  description?: string
  status: TaskStatus
  dueDate?: string
  position: number
  image?: string
  attachments?: string[]
  coverAttachment?: string
  assignees?: string[]
  createdBy?: string
  created: string
  updated: string
}

interface KanbanComment {
  id: string
  taskId: string
  body: string
  author?: string
  created: string
  updated: string
}

interface AdminUser {
  id: string
  name?: string
  email: string
  role?: 'admin' | 'editor'
}

const COLUMNS: Array<{ id: TaskStatus; label: string; hint: string }> = [
  { id: 'backlog', label: 'Backlog', hint: 'Ideias e demandas ainda não priorizadas' },
  { id: 'todo', label: 'Para Fazer', hint: 'Próximas tarefas planejadas' },
  { id: 'doing', label: 'Em andamento', hint: 'O que está sendo executado agora' },
  { id: 'done', label: 'Finalizadas', hint: 'Entregas concluídas' },
  { id: 'blocked', label: 'Impedidas', hint: 'Dependências ou bloqueios externos' },
]

const ACCEPTED_TASK_FILES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/zip',
].join(',')

type EditorState = { task?: KanbanTask; status?: TaskStatus } | null

export function TasksPage() {
  const { user } = useAuth()
  const [tasks, setTasks] = useState<KanbanTask[]>([])
  const [comments, setComments] = useState<KanbanComment[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [editor, setEditor] = useState<EditorState>(null)
  const [deleting, setDeleting] = useState<KanbanTask | null>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const userById = useMemo(() => new Map(users.map((item) => [item.id, item])), [users])

  const commentsByTask = useMemo(() => {
    const grouped = new Map<string, KanbanComment[]>()
    comments.forEach((comment) => {
      const list = grouped.get(comment.taskId) ?? []
      list.push(comment)
      grouped.set(comment.taskId, list)
    })
    return grouped
  }, [comments])

  const tasksByStatus = useMemo(() => {
    const grouped = new Map<TaskStatus, KanbanTask[]>()
    COLUMNS.forEach((column) => grouped.set(column.id, []))
    tasks.forEach((task) => grouped.get(task.status)?.push(task))
    grouped.forEach((list) => list.sort((a, b) => a.position - b.position))
    return grouped
  }, [tasks])

  const loadBoard = async () => {
    setError('')
    try {
      const [taskList, commentList, userList] = await Promise.all([
        pb.collection('kanban_tasks').getFullList<KanbanTask>({ sort: 'status,position' }),
        pb.collection('kanban_comments').getFullList<KanbanComment>({ sort: 'created' }),
        pb.collection('users').getFullList<AdminUser>({ sort: 'name,email' }).catch(() => []),
      ])
      setTasks(taskList)
      setComments(commentList)
      setUsers(userList)
    } catch {
      setError('Não foi possível carregar o quadro de tarefas.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadBoard()
  }, [])

  const saveTask = async (data: { title: string; description: string; dueDate: string; status: TaskStatus; assignees: string[]; files: File[] }) => {
    setBusy(true)
    setError('')
    try {
      const payload = new FormData()
      payload.set('title', data.title)
      payload.set('description', data.description)
      payload.set('status', data.status)
      payload.set('dueDate', data.dueDate || '')
      payload.set('assignees', JSON.stringify(data.assignees))
      const firstUploadedImage = data.files.find((file) => isImageFile(file.name, file.type))

      let saved: KanbanTask
      if (editor?.task) {
        data.files.forEach((file) => payload.append('attachments+', file))
        saved = await pb.collection('kanban_tasks').update<KanbanTask>(editor.task.id, payload)

        const previousCount = editor.task.attachments?.length ?? 0
        const appendedFiles = saved.attachments?.slice(previousCount) ?? []
        const firstNewImage = appendedFiles.find((filename) => isImageFile(filename))
        if (firstUploadedImage && firstNewImage) {
          saved = await pb.collection('kanban_tasks').update<KanbanTask>(saved.id, { coverAttachment: firstNewImage })
        }
        setTasks((current) => current.map((task) => (task.id === saved.id ? saved : task)))
      } else {
        const siblings = tasksByStatus.get(data.status) ?? []
        payload.set('position', String(siblings.length ? Math.max(...siblings.map((task) => task.position)) + 1000 : 1000))
        payload.set('createdBy', user?.name || user?.email || '')
        data.files.forEach((file) => payload.append('attachments', file))
        saved = await pb.collection('kanban_tasks').create<KanbanTask>(payload)

        if (firstUploadedImage) {
          const firstImage = saved.attachments?.find((filename) => isImageFile(filename))
          if (firstImage) saved = await pb.collection('kanban_tasks').update<KanbanTask>(saved.id, { coverAttachment: firstImage })
        }
        setTasks((current) => [...current, saved])
      }
      setEditor(null)
    } catch {
      setError('Não foi possível salvar a tarefa.')
    } finally {
      setBusy(false)
    }
  }

  const updateTaskCover = async (task: KanbanTask, filename: string) => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const saved = await pb.collection('kanban_tasks').update<KanbanTask>(task.id, { coverAttachment: filename })
      setTasks((current) => current.map((item) => (item.id === saved.id ? saved : item)))
      setEditor((current) => current?.task?.id === saved.id ? { task: saved } : current)
    } catch {
      setError('Não foi possível definir a capa da tarefa.')
    } finally {
      setBusy(false)
    }
  }

  const deleteTask = async () => {
    if (!deleting) return
    setBusy(true)
    setError('')
    try {
      await pb.collection('kanban_tasks').delete(deleting.id)
      const taskComments = comments.filter((comment) => comment.taskId === deleting.id)
      await Promise.all(taskComments.map((comment) => pb.collection('kanban_comments').delete(comment.id).catch(() => null)))
      setTasks((current) => current.filter((task) => task.id !== deleting.id))
      setComments((current) => current.filter((comment) => comment.taskId !== deleting.id))
      setDeleting(null)
    } catch {
      setError('Não foi possível apagar a tarefa.')
    } finally {
      setBusy(false)
    }
  }

  const addComment = async (taskId: string, body: string) => {
    const text = body.trim()
    if (!text) return
    setBusy(true)
    setError('')
    try {
      const saved = await pb.collection('kanban_comments').create<KanbanComment>({
        taskId,
        body: text,
        author: user?.name || user?.email || '',
      })
      setComments((current) => [...current, saved])
    } catch {
      setError('Não foi possível adicionar o comentário.')
    } finally {
      setBusy(false)
    }
  }

  const moveTask = async (taskId: string, status: TaskStatus) => {
    const task = tasks.find((item) => item.id === taskId)
    if (!task || busy) return
    const targetList = tasks.filter((item) => item.status === status && item.id !== taskId)
    const position = targetList.length ? Math.max(...targetList.map((item) => item.position)) + 1000 : 1000
    setDraggingId(null)
    setTasks((current) => current.map((item) => (item.id === taskId ? { ...item, status, position } : item)))
    try {
      await pb.collection('kanban_tasks').update(taskId, { status, position })
    } catch {
      setError('Movi localmente, mas não consegui salvar a nova coluna.')
      setTasks((current) => current.map((item) => (item.id === taskId ? task : item)))
    }
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <div>
          <span className="tasks-kicker">LORENA MAGAZINE</span>
          <h1>Tarefas e Projetos</h1>
          <p>Quadro simples para organizar demandas internas, responsáveis, documentos, comentários e entregas.</p>
        </div>
        <button type="button" onClick={() => { setError(''); setEditor({ status: 'backlog' }) }}><Plus size={16} />Nova tarefa</button>
      </div>

      {error && !editor && !deleting && <p className="tasks-error">{error}</p>}
      {loading ? <p className="tasks-loading">Carregando quadro…</p> : (
        <div className="kanban-board">
          {COLUMNS.map((column) => {
            const columnTasks = tasksByStatus.get(column.id) ?? []
            return <section key={column.id} className={`kanban-column is-${column.id}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); const id = event.dataTransfer.getData('text/plain') || draggingId; if (id) void moveTask(id, column.id) }}>
              <header>
                <div><h2>{column.label}</h2><span>{column.hint}</span></div>
                <strong>{String(columnTasks.length).padStart(2, '0')}</strong>
              </header>
              <button type="button" className="kanban-column-add" onClick={() => { setError(''); setEditor({ status: column.id }) }}><Plus size={14} />Adicionar</button>
              <div className="kanban-card-list">
                {columnTasks.map((task) => <TaskCard key={task.id} task={task} users={users} userById={userById} commentsCount={commentsByTask.get(task.id)?.length ?? 0} isDragging={draggingId === task.id} onDragStart={(event) => { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', task.id); setDraggingId(task.id) }} onDragEnd={() => setDraggingId(null)} onEdit={() => { setError(''); setEditor({ task }) }} onDelete={() => { setError(''); setDeleting(task) }} />)}
                {!columnTasks.length && <div className="kanban-empty">Sem cards nesta coluna.</div>}
              </div>
            </section>
          })}
        </div>
      )}

      {editor && <TaskEditorModal state={editor} users={users} comments={editor.task ? commentsByTask.get(editor.task.id) ?? [] : []} busy={busy} error={error} onClose={() => !busy && setEditor(null)} onSave={saveTask} onComment={addComment} onSetCover={updateTaskCover} />}

      {deleting && (
        <div className="modal-backdrop" onClick={() => !busy && setDeleting(null)}>
          <div className="modal-card task-modal" role="dialog" aria-modal="true" aria-labelledby="delete-task-title" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header"><h2 id="delete-task-title">Apagar tarefa?</h2></div>
            <p className="materia-delete-copy">A tarefa, anexos e comentários serão removidos do quadro.</p>
            {error && <p className="materia-action-error">{error}</p>}
            <div className="materia-action-buttons">
              <button type="button" className="secondary" disabled={busy} onClick={() => setDeleting(null)}>Cancelar</button>
              <button type="button" className="danger-button" disabled={busy} onClick={deleteTask}>{busy ? 'Apagando…' : 'Apagar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function TaskCard({ task, users, userById, commentsCount, isDragging, onDragStart, onDragEnd, onEdit, onDelete }: { task: KanbanTask; users: AdminUser[]; userById: Map<string, AdminUser>; commentsCount: number; isDragging: boolean; onDragStart: (event: DragEvent<HTMLElement>) => void; onDragEnd: () => void; onEdit: () => void; onDelete: () => void }) {
  const coverUrl = getTaskCoverUrl(task)
  const assignedUsers = getAssignedUsers(task, users, userById)
  const attachmentsCount = (task.attachments?.length ?? 0) + (task.image ? 1 : 0)

  return <article className={`kanban-card${isDragging ? ' is-dragging' : ''}`} draggable onDragStart={onDragStart} onDragEnd={onDragEnd}>
    {coverUrl && <img src={coverUrl} alt="" />}
    <div className="kanban-card-body">
      <h3>{task.title}</h3>
      {task.description && <p>{plainText(task.description)}</p>}
      {assignedUsers.length > 0 && <div className="kanban-assignees" aria-label="Responsáveis">
        {assignedUsers.slice(0, 4).map((item) => <span key={item.id} title={getUserLabel(item)}>{getInitials(item)}</span>)}
        {assignedUsers.length > 4 && <span>+{assignedUsers.length - 4}</span>}
      </div>}
      <div className="kanban-card-meta">
        {task.dueDate && <span><Calendar size={13} />{formatDate(task.dueDate)}</span>}
        <span><MessageSquare size={13} />{commentsCount}</span>
        {attachmentsCount > 0 && <span><Paperclip size={13} />{attachmentsCount}</span>}
      </div>
    </div>
    <div className="kanban-card-actions">
      <button type="button" onClick={onEdit} aria-label={`Editar ${task.title}`}><Pencil size={14} /></button>
      <button type="button" onClick={onDelete} aria-label={`Apagar ${task.title}`}><Trash2 size={14} /></button>
    </div>
  </article>
}

function TaskEditorModal({ state, users, comments, busy, error, onClose, onSave, onComment, onSetCover }: { state: Exclude<EditorState, null>; users: AdminUser[]; comments: KanbanComment[]; busy: boolean; error: string; onClose: () => void; onSave: (data: { title: string; description: string; dueDate: string; status: TaskStatus; assignees: string[]; files: File[] }) => void; onComment: (taskId: string, body: string) => void; onSetCover: (task: KanbanTask, filename: string) => void }) {
  const task = state.task
  const [title, setTitle] = useState(task?.title ?? '')
  const [description, setDescription] = useState(task?.description ?? '')
  const [dueDate, setDueDate] = useState(toDateInputValue(task?.dueDate))
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? state.status ?? 'backlog')
  const [assignees, setAssignees] = useState<string[]>(normalizeAssignees(task?.assignees))
  const [files, setFiles] = useState<File[]>([])
  const [comment, setComment] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim()) return
    onSave({ title: title.trim(), description: description.trim(), dueDate, status, assignees, files })
  }

  const selectedFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files ?? [])
    if (!nextFiles.length) return
    setFiles((current) => [...current, ...nextFiles])
    event.target.value = ''
  }

  const toggleAssignee = (id: string) => {
    setAssignees((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
  }

  return <div className="modal-backdrop" onClick={onClose}>
    <form className="modal-card task-modal" role="dialog" aria-modal="true" aria-labelledby="task-editor-title" onSubmit={submit} onClick={(event) => event.stopPropagation()}>
      <div className="modal-header">
        <h2 id="task-editor-title">{task ? 'Editar tarefa' : 'Nova tarefa'}</h2>
        <button type="button" className="bio-modal-close" onClick={onClose} aria-label="Fechar"><X size={18} /></button>
      </div>
      <div className="task-form-grid">
        <label>Título<input value={title} onChange={(event) => setTitle(event.target.value)} required maxLength={180} placeholder="Ex.: Publicar novo carrossel" /></label>
        <label>Status<select value={status} onChange={(event) => setStatus(event.target.value as TaskStatus)}>{COLUMNS.map((column) => <option key={column.id} value={column.id}>{column.label}</option>)}</select></label>
        <label>Data<input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} /></label>
      </div>

      <div className="task-section">
        <h3><UserRound size={15} />Responsáveis</h3>
        {users.length ? <div className="task-user-grid">
          {users.map((item) => <label key={item.id} className={assignees.includes(item.id) ? 'is-selected' : ''}>
            <input type="checkbox" checked={assignees.includes(item.id)} onChange={() => toggleAssignee(item.id)} />
            <span>{getInitials(item)}</span>
            <strong>{getUserLabel(item)}</strong>
          </label>)}
        </div> : <p className="task-section-empty">Nenhum usuário disponível para seleção.</p>}
      </div>

      <label className="task-description-label">Descrição<textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Contexto, checklist, links, critérios de conclusão..." /></label>

      <div className="task-section">
        <h3><Paperclip size={15} />Anexos</h3>
        <label className="task-file-picker">
          <ImagePlus size={22} />
          <span>Adicionar imagens ou documentos</span>
          <small>PDF, Office, CSV, ZIP e imagens. Até 200MB por arquivo.</small>
          <input className="visually-hidden" type="file" multiple accept={ACCEPTED_TASK_FILES} onChange={selectedFiles} />
        </label>

        {files.length > 0 && <div className="task-new-files">
          <strong>Arquivos selecionados para envio</strong>
          {files.map((file, index) => <div key={`${file.name}-${file.lastModified}-${index}`}><FileText size={14} />{file.name}<button type="button" onClick={() => setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))}>remover</button></div>)}
        </div>}

        {task && <AttachmentList task={task} busy={busy} onSetCover={onSetCover} />}
      </div>

      {task && <div className="task-comments">
        <h3>Comentários</h3>
        <div className="task-comment-list">
          {comments.length ? comments.map((item) => <article key={item.id}><strong>{item.author || 'Equipe'}</strong><span>{formatDateTime(item.created)}</span><p>{plainText(item.body)}</p></article>) : <p className="task-comment-empty">Nenhum comentário ainda.</p>}
        </div>
        <div className="task-comment-form">
          <textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Adicionar comentário..." />
          <button type="button" disabled={busy || !comment.trim()} onClick={() => { void onComment(task.id, comment); setComment('') }}>Comentar</button>
        </div>
      </div>}

      {error && <p className="materia-action-error">{error}</p>}
      <div className="materia-action-buttons">
        <button type="button" className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button type="submit" disabled={busy || !title.trim()}>{busy ? 'Salvando…' : 'Salvar tarefa'}</button>
      </div>
    </form>
  </div>
}

function AttachmentList({ task, busy, onSetCover }: { task: KanbanTask; busy: boolean; onSetCover: (task: KanbanTask, filename: string) => void }) {
  const attachments = task.attachments ?? []
  const hasLegacyImage = Boolean(task.image)

  if (!attachments.length && !hasLegacyImage) {
    return <p className="task-section-empty">Nenhum anexo salvo.</p>
  }

  return <div className="task-attachment-list">
    {attachments.map((filename) => {
      const image = isImageFile(filename)
      const url = taskFileUrl(task, filename)
      const activeCover = task.coverAttachment === filename || (!task.coverAttachment && image && getTaskCoverFilename(task) === filename)
      return <article key={filename} className={activeCover ? 'is-cover' : ''}>
        {image ? <img src={url} alt="" /> : <span className="task-file-icon"><FileText size={18} /></span>}
        <div>
          <strong>{filename}</strong>
          <small>{image ? 'Imagem' : 'Documento'}</small>
        </div>
        <div className="task-attachment-actions">
          <a href={url} download target="_blank" rel="noreferrer" aria-label={`Baixar ${filename}`}><Download size={14} />Baixar</a>
          {image && <button type="button" disabled={busy || activeCover} onClick={() => onSetCover(task, filename)}><Star size={14} />{activeCover ? 'Capa atual' : 'Usar como capa'}</button>}
        </div>
      </article>
    })}
    {task.image && <article className={!task.coverAttachment && !attachments.some((filename) => isImageFile(filename)) ? 'is-cover' : ''}>
      <img src={resolveFileUrl('kanban_tasks', task.id, task.image, task.updated)} alt="" />
      <div>
        <strong>{task.image}</strong>
        <small>Imagem legada</small>
      </div>
      <div className="task-attachment-actions">
        <a href={resolveFileUrl('kanban_tasks', task.id, task.image, task.updated)} download target="_blank" rel="noreferrer"><Download size={14} />Baixar</a>
      </div>
    </article>}
  </div>
}

function getTaskCoverFilename(task: KanbanTask) {
  const attachments = task.attachments ?? []
  if (task.coverAttachment && attachments.includes(task.coverAttachment)) return task.coverAttachment
  return attachments.find((filename) => isImageFile(filename))
}

function getTaskCoverUrl(task: KanbanTask) {
  const coverFilename = getTaskCoverFilename(task)
  if (coverFilename) return taskFileUrl(task, coverFilename)
  if (task.image) return resolveFileUrl('kanban_tasks', task.id, task.image, task.updated)
  return ''
}

function taskFileUrl(task: KanbanTask, filename: string) {
  return resolveFileUrl('kanban_tasks', task.id, filename, task.updated)
}

function isImageFile(filename: string, mimeType = '') {
  return mimeType.startsWith('image/') || /\.(png|jpe?g|webp|gif|avif|bmp|tiff?)$/i.test(filename)
}

function normalizeAssignees(value?: string[]) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function getAssignedUsers(task: KanbanTask, users: AdminUser[], userById: Map<string, AdminUser>) {
  return normalizeAssignees(task.assignees).map((id) => userById.get(id)).filter((item): item is AdminUser => Boolean(item)).sort((a, b) => users.indexOf(a) - users.indexOf(b))
}

function getUserLabel(user: AdminUser) {
  return user.name || user.email
}

function getInitials(user: AdminUser) {
  const label = getUserLabel(user).trim()
  const parts = label.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return label.slice(0, 2).toUpperCase()
}

function toDateInputValue(value?: string) {
  if (!value) return ''
  return value.slice(0, 10)
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value))
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}

function plainText(value: string) {
  const div = document.createElement('div')
  div.innerHTML = value
  return (div.textContent || div.innerText || value).trim()
}
